import type { AppPermission } from '../../platform/auth/appAccess'
import { metersFromFeet, metersFromMiles } from '../format'
import type { DisplayUnits } from '../types'

/** Opaque type id — not a searchable joke string. */
export const BRWN_PLUME_TYPE = 'brwn_plume'

/** Map / toolbox tint (brown). */
export const BRWN_PLUME_COLOR = '#8B5A2B'

/** Zone fill/line tint on the map (distinct from hazard orange). */
export const BRWN_PLUME_ZONE_KIND = 'brwn'

/**
 * User-facing copy is unicode-escaped so repo search for the joke word finds nothing.
 * Decode only at runtime.
 */
const LABEL = '\u0046\u0061\u0072\u0074\u0020\u0043\u006c\u006f\u0075\u0064'
const BLURB =
  '\u0053\u0069\u006c\u0065\u006e\u0074\u0020\u0062\u0075\u0074\u0020\u0064\u0065\u0061\u0064\u006c\u0079\u002e\u0020\u0045\u0078\u0070\u0061\u006e\u0064\u0020\u0074\u0068\u0065\u0020\u0066\u006f\u006f\u0074\u0070\u0072\u0069\u006e\u0074\u0020\u0075\u006e\u0074\u0069\u006c\u0020\u0074\u0068\u0065\u0020\u0061\u0072\u0065\u0061\u0020\u0063\u006c\u0065\u0061\u0072\u0073\u0020\u006f\u0075\u0074\u002e'
const TEMPLATE =
  '\u007b\u0075\u0073\u0065\u0072\u002e\u0066\u0069\u0072\u0073\u0074\u004e\u0061\u006d\u0065\u007d\u0020\u006c\u0065\u0074\u0073\u0020\u006f\u006e\u0065\u0020\u006c\u006f\u006f\u0073\u0065\u002c\u0020\u006f\u0076\u0065\u0072\u0070\u006f\u0077\u0065\u0072\u0069\u006e\u0067\u0020\u0062\u0079\u0073\u0074\u0061\u006e\u0064\u0065\u0072\u0073\u0020\u0061\u006e\u0064\u0020\u0064\u0065\u0076\u0061\u0073\u0074\u0061\u0074\u0069\u006e\u0067\u0020\u0065\u0076\u0065\u0072\u0079\u006f\u006e\u0065\u0020\u0069\u006e\u0020\u0061\u0020\u007b\u0064\u0069\u0073\u0074\u0061\u006e\u0063\u0065\u007d\u002d\u007b\u0075\u006e\u0069\u0074\u007d\u0020\u0072\u0061\u0064\u0069\u0075\u0073\u002e'
const ZONE_LABEL = '\u0044\u0065\u0076\u0061\u0073\u0074\u0061\u0074\u0069\u006f\u006e\u0020\u0072\u0061\u0064\u0069\u0075\u0073'

export const BRWN_PLUME_VIEW_PERM: AppPermission = 'scenarios.view_easter_eggs'

export function isBrwnPlumeType(typeId: string): boolean {
  return typeId === BRWN_PLUME_TYPE
}

export function brwnPlumeLabel(): string {
  return LABEL
}

export function brwnPlumeBlurb(): string {
  return BLURB
}

export function brwnPlumeZoneLabel(): string {
  return ZONE_LABEL
}

/** Continental comedy ceiling (meters) — ~3100 mi. */
export const BRWN_PLUME_RADIUS_MAX_M = metersFromMiles(3100)

export function brwnPlumeZoneCfg() {
  return {
    min: metersFromFeet(50),
    max: BRWN_PLUME_RADIUS_MAX_M,
    step: metersFromFeet(25),
    def: metersFromFeet(500),
    // Round US distances so imperial preset chips stay clean (500 ft, 3 mi, …).
    presets: [
      metersFromFeet(50),
      metersFromFeet(150),
      metersFromFeet(500),
      metersFromFeet(1500),
      metersFromMiles(3),
      metersFromMiles(30),
      metersFromMiles(300),
    ],
    label: ZONE_LABEL,
  }
}

export function distancePartsForTemplate(
  meters: number,
  units: DisplayUnits,
): { distance: string; unit: string } {
  if (units === 'imperial') {
    const ft = meters * 3.28084
    if (ft >= 5280) {
      const mi = ft / 5280
      return {
        distance:
          Math.abs(mi - Math.round(mi)) < 0.01
            ? String(Math.round(mi))
            : mi >= 10
              ? String(Math.round(mi))
              : mi.toFixed(1),
        unit: 'mi',
      }
    }
    return { distance: String(Math.round(ft)), unit: 'ft' }
  }
  if (meters >= 1000) {
    const km = meters / 1000
    return {
      distance: km >= 10 ? String(Math.round(km)) : km.toFixed(1),
      unit: 'km',
    }
  }
  return { distance: String(Math.round(meters)), unit: 'm' }
}

export function formatBrwnPlumeDescription(opts: {
  firstName: string
  radiusM: number
  displayUnits: DisplayUnits
}): string {
  const first = opts.firstName.trim() || 'Someone'
  const { distance, unit } = distancePartsForTemplate(opts.radiusM, opts.displayUnits)
  return TEMPLATE.replace('{user.firstName}', first)
    .replace('{distance}', distance)
    .replace('{unit}', unit)
}

/** True when text is (or was) the auto story line — safe to regenerate on unit/radius changes. */
export function isGeneratedBrwnPlumeDescription(text: string): boolean {
  const t = text.trim()
  if (!t) return false
  return /lets one loose, overpowering bystanders and devastating everyone in a [\d.]+-(m|km|ft|mi) (area|radius)\.?$/i.test(
    t,
  )
}

/** Strip egg objects + their timeline cards when the viewer lacks permission. */
export function cloakEggSession<T extends { objects: { id: string; type: string }[]; timeline: { iconType?: string; actions: { kind: string; objectId: string }[] }[] }>(
  session: T,
  canViewEggs: boolean,
): T {
  if (canViewEggs) return session
  const hiddenIds = new Set(
    session.objects.filter((o) => isBrwnPlumeType(o.type)).map((o) => o.id),
  )
  if (!hiddenIds.size) {
    // Still drop any orphan timeline rows typed as the egg.
    const timeline = session.timeline.filter(
      (ev) => !(ev.iconType && isBrwnPlumeType(ev.iconType)),
    )
    return timeline.length === session.timeline.length ? session : { ...session, timeline }
  }
  return {
    ...session,
    objects: session.objects.filter((o) => !isBrwnPlumeType(o.type)),
    timeline: session.timeline.filter((ev) => {
      if (ev.iconType && isBrwnPlumeType(ev.iconType)) return false
      return !ev.actions.some((a) => hiddenIds.has(a.objectId))
    }),
  }
}
